/**
 * Adapted from react-use useLockBodyScroll (https://github.com/streamich/react-use/blob/master/src/useLockBodyScroll.ts)
 *
 * @remarks
 * Changes from upstream:
 * - Removed iOS touchmove branch (unnecessary since iOS 13+)
 * - Removed elementRef/iframe lookup
 * - Simplified to document.body-only with counter-based nested lock support
 *
 * @module useBodyScrollLock
 */

import { useEffect, useRef } from 'react';

type BodyLockInfo = {
  counter: number;
  initialOverflow: CSSStyleDeclaration['overflow'];
};

const getDocument = () => (typeof document === 'object' ? document : undefined);

let bodyLockInfo: BodyLockInfo | null = null;

const lockBody = (body: HTMLElement) => {
  if (!bodyLockInfo) {
    bodyLockInfo = {
      counter: 1,
      initialOverflow: body.style.overflow,
    };

    body.style.overflow = 'hidden';

    return;
  }

  bodyLockInfo = {
    ...bodyLockInfo,
    counter: bodyLockInfo.counter + 1,
  };
};

const unlockBody = (body: HTMLElement) => {
  if (!bodyLockInfo) return;

  if (bodyLockInfo.counter === 1) {
    const { initialOverflow } = bodyLockInfo;

    bodyLockInfo = null;

    body.style.overflow = initialOverflow;

    return;
  }

  bodyLockInfo = {
    ...bodyLockInfo,
    counter: bodyLockInfo.counter - 1,
  };
};

/**
 * Locks `document.body` scroll by setting `overflow: hidden`.
 * Supports nested locks via a shared counter — the last consumer
 * to unlock restores the original overflow value.
 *
 * @param locked - Whether body scroll should be locked. Defaults to `true`.
 */
const useBodyScrollLock = (locked = true) => {
  const bodyRef = useRef<HTMLElement | null>(getDocument()?.body ?? null);

  useEffect(() => {
    const body = bodyRef.current;

    if (!body) return;

    if (locked) {
      lockBody(body);

      return;
    }

    unlockBody(body);
  }, [locked]);

  useEffect(() => {
    const body = bodyRef.current;

    if (!body) return;

    return () => {
      unlockBody(body);
    };
  }, []);
};

export default useBodyScrollLock;
